import { CalendarCheck, Check } from 'lucide-react';
import * as React from 'react';

import { useAnswerConnectionRequest } from './api';
import {
  ACCEPT_LABEL,
  DECLINE_LABEL,
  WANTS_TO_CONNECT_PILL,
  initialsOf,
  personName,
  roleAtCompany,
} from './constants';
import type { ConnectionRequest } from './types';

/**
 * One inbound request, with its two answers (`MEM-032` AC-6, AC-7).
 *
 * ## Designed rather than transcribed, and the specification says so
 *
 * There is no prototype for this. The specification's own gap list records it: *"Neither
 * prototype implements the inbound side of a connection request — there is no request list, no
 * accept and no decline… MEM-CON-005 to MEM-CON-007 must be designed."* So this row is composed
 * from shapes the member app already has — the connections row's card treatment and initials
 * tile, the detail's *"Met at"* badge, and the composer's message block — rather than invented.
 *
 * ## Accept and Decline are the same weight
 *
 * Decline is not a destructive control and is not styled as one. `MEM-032` AC-7 makes declining
 * private and unannounced, and the story's note says the recipient *"should be able to decline
 * without it being an act"*. A red button makes it one.
 *
 * ## The message is shown, because reading it is the decision
 *
 * `MEM-CON-004`'s note is the one thing a member may send somebody they are not yet connected to
 * (`MEM-034` AC-6), and it exists so the recipient can decide with something in front of them. A
 * request with no message renders without the block rather than with an empty quote.
 *
 * It was ADR 032's single exception to there being no messaging at all. Under ADR 052 it is
 * something more consequential rather than less: accepting turns this note into the first message
 * on the pair's thread, so what the recipient is reading here is the opening line of a
 * conversation they are deciding whether to have.
 *
 * ## What the 10 September prototype added, and what it did not
 *
 * `Web App.dc.html` §3.10 draws the card the plan's Phase 3 builds this against: a 46px avatar and
 * the name over the role, a **status pill** at the right reading *Wants to connect*, the message on
 * `--surface-sunken`, and **Accept before Decline**. Every one of those is taken. Its *accepted* and
 * *declined* pills are not: an answered request is served to nobody (see `repository.ts`), so a card
 * that could show one would be a card for a row that does not come back.
 */
export function ConnectionRequestCard({
  request,
  showEventPill = true,
}: {
  readonly request: ConnectionRequest;
  /**
   * The event badge, which a per-event requests page does not draw: its header card already names
   * the evening, and a pill repeating it under every row is the same fact three times.
   */
  readonly showEventPill?: boolean;
}): React.ReactElement {
  const answer = useAnswerConnectionRequest();
  const name = personName(request);
  const line = roleAtCompany(request);
  const busy = answer.isPending;

  return (
    <article className="space-y-3 rounded-lg border border-border-subtle bg-card p-3.5 shadow-xs md:p-[18px]">
      <div className="flex flex-wrap items-center gap-3 md:gap-[13px]">
        <span
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-pill bg-brand-100 font-brand text-[15px] font-bold text-brand-700 md:h-[46px] md:w-[46px]"
          aria-hidden
        >
          {initialsOf(request)}
        </span>
        <div className="min-w-0 flex-1 basis-32">
          <p className="truncate font-brand text-sm font-bold text-foreground md:text-md">{name}</p>
          {line === null ? null : (
            <p className="truncate text-xs text-muted-foreground md:text-sm">{line}</p>
          )}
        </div>
        {/* §3.10's status pill. Only one state ever renders, because only pending requests are
            served — the prototype's Connected and Declined pills describe rows that never arrive. */}
        <span className="inline-flex flex-none items-center whitespace-nowrap rounded-pill bg-fuchsia-50 px-2.5 py-1 font-brand text-2xs font-bold uppercase tracking-[0.04em] text-brand-accent">
          {WANTS_TO_CONNECT_PILL}
        </span>
      </div>

      {!showEventPill || request.eventName === null ? null : (
        <p className="inline-flex items-center gap-1.5 rounded-pill bg-brand-100 px-2.5 py-1 font-brand text-2xs font-bold text-brand-700">
          <CalendarCheck className="h-3 w-3" aria-hidden />
          {request.eventName}
        </p>
      )}

      {request.message === null ? null : (
        <p className="rounded-md bg-muted px-[15px] py-3.5 text-sm leading-snug text-text-body">
          {request.message}
        </p>
      )}

      {/* §3.10: Accept first, in the filled primary with a `check`; Decline beside it in the
          secondary outline. **Neither is destructive and neither is styled as one** — `MEM-032`
          AC-7 makes declining private and unannounced, and a red button makes it an act. */}
      <div className="flex flex-wrap gap-2.5">
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            answer.mutate({ requesterRef: request.requesterRef, answer: 'accept' });
          }}
          className="inline-flex flex-1 items-center justify-center gap-[7px] rounded-pill bg-brand px-[22px] py-2.5 font-brand text-sm font-bold text-white disabled:opacity-60 sm:flex-none"
        >
          <Check className="h-4 w-4" aria-hidden />
          {ACCEPT_LABEL}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            answer.mutate({ requesterRef: request.requesterRef, answer: 'decline' });
          }}
          className="inline-flex flex-1 items-center justify-center rounded-pill border-[1.5px] border-border bg-card px-5 py-2.5 font-brand text-sm font-bold text-muted-foreground disabled:opacity-60 sm:flex-none"
        >
          {DECLINE_LABEL}
        </button>
      </div>
    </article>
  );
}
